import { DateTimeFormatter, LocalTime } from "@js-joda/core";
import { API } from "../../../API";
import { ApiWithRetry } from "../../../ExponentialBackOffNetworkRequest";
import { formatDay } from "../../../format";
import {
  CaregiverAnomalyFilter,
  CaregiverAvailability,
  DayAvailability,
  NullableCaregiverAvailability,
} from "../../../messages/Caregiver";

export async function getAnomalies(api: API, noEncode?: boolean): Promise<string> {
  let result = "";
  const NEWLINE = noEncode === true ? "\n" : "%0D%0A";

  const anomalies = await getAnomaliesArray(api);

  if (anomalies.length !== 0) {
    result += NEWLINE + NEWLINE + "Availability setting issues were found: " + NEWLINE;
  } else {
    result += NEWLINE + "No availability setting issues were found." + NEWLINE;
  }

  anomalies.forEach((anomaly) => {
    result += anomaly + NEWLINE;
  });

  return result;
}

export async function getAnomaliesArray(api: API): Promise<string[]> {
  const request = new ApiWithRetry();
  const availability: NullableCaregiverAvailability | null = await request.fetch(() =>
    api.getCaregiverAvailability()
  );
  function checkMaxDistance(caregiverValue: number, compValue: number): string[] {
    if (caregiverValue <= compValue) {
      return [`Maximum patient's radius is too small: ${caregiverValue}`];
    }
    return [];
  }

  function checkMaxDuration(caregiverValue: number, compValue: number): string[] {
    if (caregiverValue <= compValue) {
      return [`Max visit duration is too short: ${caregiverValue}`];
    }
    return [];
  }

  function checkMinDuration(caregiverValue: number, compValue: number): string[] {
    if (caregiverValue >= compValue) {
      return [`Minimum visit duration is too long: ${caregiverValue}`];
    }
    return [];
  }

  function checkDurationDiff(
    caregiver: CaregiverAvailability,
    desiredDifference: number
  ): string[] {
    const desDiff = desiredDifference;
    const anomalies: string[] = [];
    if (caregiver.maxDuration - caregiver.minDuration <= desDiff) {
      anomalies.push(
        `Work hours' difference is too small: ${caregiver.maxDuration - caregiver.minDuration}.`
      );
    }
    // Checking for the same hour difference for each day of the week
    caregiver.dayAvailabilities.forEach((day, index) => {
      const diff = day.endHour - day.startHour;
      if (diff <= desDiff) {
        const endTime = LocalTime.of(day.endHour % 24).format(DateTimeFormatter.ofPattern("HH:mm"));
        const startTime = LocalTime.of(day.startHour % 24).format(
          DateTimeFormatter.ofPattern("HH:mm")
        );
        anomalies.push(
          `Working hours on ${formatDay(
            index
          )} are too short: ${diff} hour/s. (${startTime} - ${endTime})`
        );
      }
    });

    return anomalies;
  }

  function checkBlockedDays(dayAvailabilities: DayAvailability[], threshold: number): string[] {
    let blockedDays = 0;
    let index = 0;
    const days = [];
    for (const dayInfo of dayAvailabilities) {
      if (dayInfo.busyAllDay) {
        blockedDays++;
        days.push(formatDay(index));
      }
      index++;
    }
    if (blockedDays > threshold) {
      const anomalies = [];
      anomalies.push(
        `Too many unavailable days: ${blockedDays}. Unavailable days: ${days.join(", ")}`
      );
      return anomalies;
    }

    return [];
  }

  function checkSpecificDays(dayAvailabilities: DayAvailability[], checkDays: number[]): string[] {
    const anomalies: string[] = [];
    for (const day of checkDays) {
      if (dayAvailabilities[day].busyAllDay) {
        anomalies.push(`Caregiver does not work on ${formatDay(day)}s`);
      }
    }
    return anomalies;
  }

  const filter: CaregiverAnomalyFilter = {
    minDistance: 4,
    maxDuration: 4,
    minDuration: 4,
    durationDifference: 4,
    blockedDays: 3,
    specifiedBlockedDays: undefined,
  };

  if (availability !== null && availability.caregiverAvailability !== null) {
    const maxDistanceAnom =
      filter.minDistance !== undefined
        ? checkMaxDistance(availability.caregiverAvailability.maxDistance, filter.minDistance)
        : [];
    const maxDurationAnom =
      filter.maxDuration !== undefined
        ? checkMaxDuration(availability.caregiverAvailability.maxDuration, filter.maxDuration)
        : [];
    const minDurationAnom =
      filter.minDuration !== undefined
        ? checkMinDuration(availability.caregiverAvailability.minDuration, filter.minDuration)
        : [];
    const durationDiffAnom =
      filter.durationDifference !== undefined
        ? checkDurationDiff(availability.caregiverAvailability, filter.durationDifference)
        : [];
    const blockedDaysAnom =
      filter.blockedDays !== undefined
        ? checkBlockedDays(availability.caregiverAvailability.dayAvailabilities, filter.blockedDays)
        : [];
    const specifiedBlockedDays =
      filter.specifiedBlockedDays === undefined
        ? []
        : checkSpecificDays(
            availability.caregiverAvailability.dayAvailabilities,
            filter.specifiedBlockedDays
          );

    const anomalies: string[] = [
      ...maxDistanceAnom,
      ...maxDurationAnom,
      ...minDurationAnom,
      ...durationDiffAnom,
      ...blockedDaysAnom,
      ...specifiedBlockedDays,
    ];

    return anomalies;
  } else {
    return [];
  }
}
